import { z } from 'zod'
import { oc } from '@orpc/contract'

/**
 * Population Health analytics (PD-1715, docs/POPULATION.md): deterministic reads over the
 * Risks & Gaps findings history (patient_measure_finding) and the mart.patient view.
 * One endpoint per dashboard section; all of them read as the caller, so they see only the
 * patients the caller may see in Medplum, and all of them return the definition text their
 * info icons show.
 *
 * Browser-safe (oc + zod only): the value lists below are what the frontends import.
 */

export const POPULATION_PERIODS = [
  'last-30-days',
  'last-90-days',
  'last-365-days',
  'previous-calendar-year',
  'year-to-date',
  'custom',
] as const
export const POPULATION_COMPARISONS = ['previous-period', 'same-dates-last-year'] as const
/** The longest `custom` period, in days. */
export const POPULATION_MAX_CUSTOM_DAYS = 366
/** Mirrors the mock's age filter; the bands overlap ('18-plus' includes the other two). */
export const POPULATION_AGE_GROUPS = ['18-plus', '45-64', '65-plus'] as const
export const POPULATION_RISK_LEVELS = ['high', 'medium'] as const
/** Mirrors CPG_MEASURE_KIND_VALUES (cpg-registry.contract.ts). */
export const POPULATION_KINDS = ['risk', 'gap'] as const
export const POPULATION_MEASURE_STATUSES = ['improving', 'stable', 'declining'] as const

export type PopulationPeriod = (typeof POPULATION_PERIODS)[number]
export type PopulationComparison = (typeof POPULATION_COMPARISONS)[number]
export type PopulationAgeGroup = (typeof POPULATION_AGE_GROUPS)[number]
export type PopulationRiskLevel = (typeof POPULATION_RISK_LEVELS)[number]
export type PopulationKind = (typeof POPULATION_KINDS)[number]

/** Population filters, applied after clinical eligibility (a finding) is established. */
const filtersShape = {
  /** Attributed provider (Practitioner id). */
  providerId: z.string().min(1).optional(),
  /** Two-letter address state, e.g. 'NY', or 'unknown'. */
  state: z.string().min(1).optional(),
  ageGroup: z.enum(POPULATION_AGE_GROUPS).optional(),
  /** Secondary filter; never decides family membership. */
  riskLevel: z.enum(POPULATION_RISK_LEVELS).optional(),
  kind: z.enum(POPULATION_KINDS).optional(),
}

/**
 * A boolean that survives GET query-string transport: OpenAPILink stringifies query values
 * and no coercion is configured, so accept 'true'/'false' as well (see audit.contract.ts).
 */
const queryBooleanSchema = z.union([
  z.boolean(),
  z.enum(['true', 'false']).transform((value) => value === 'true'),
])

/** A UTC calendar day, 'YYYY-MM-DD'. */
const daySchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/)

/**
 * A reporting period, its comparison, and the filters — flat so they travel as query params.
 * `start` and `end` (inclusive) are the range of period 'custom' and are rejected with any
 * other period; the handlers check them against today (BAD_REQUEST): start ≤ end ≤ today,
 * at most POPULATION_MAX_CUSTOM_DAYS.
 */
const periodQuerySchema = z.object({
  period: z.enum(POPULATION_PERIODS),
  comparison: z.enum(POPULATION_COMPARISONS),
  start: daySchema.optional(),
  end: daySchema.optional(),
  ...filtersShape,
})

/** Inclusive calendar dates, UTC. */
const dateRangeSchema = z.object({ start: z.string(), end: z.string() })

const windowSchema = z.object({
  current: dateRangeSchema,
  comparison: dateRangeSchema,
  /** Latest finding date (`generated_at`) in the history; null when there are no findings. */
  dataThrough: z.string().nullable(),
  /**
   * The older of the last findings rebuild and the last warehouse refresh (ISO instant), so
   * everything shown is at least this fresh; null until both have run.
   */
  refreshedAt: z.string().nullable(),
})

/**
 * Findings closed in one period and their median time to resolve, in days (one decimal);
 * `medianDays` is null when none closed.
 */
const closureTimeSchema = z.object({
  closed: z.number().int(),
  medianDays: z.number().nullable(),
})

/**
 * A period's time to resolve next to its comparison's; `changeDays` is current − comparison
 * (negative = faster), null when either resolved nothing.
 */
const closureChangeSchema = z.object({
  current: closureTimeSchema,
  comparison: closureTimeSchema,
  changeDays: z.number().nullable(),
})

/** A count next to its comparison's; `changePercent` is null when the comparison is 0. */
const countChangeSchema = z.object({
  value: z.number().int(),
  comparisonValue: z.number().int(),
  changePercent: z.number().nullable(),
})

const labelSchema = z.object({ slug: z.string(), label: z.string() })

export const populationSummary = oc
  .route({ method: 'GET', path: '/v2/population/summary' })
  .input(periodQuerySchema)
  .output(
    z.object({
      window: windowSchema,
      attributedPatients: z.object({ value: z.number().int(), definition: z.string() }),
      highRiskPatients: countChangeSchema.extend({
        /** High-risk at the period's end and not at its start. */
        newInPeriod: z.number().int(),
        definition: z.string(),
      }),
      openCareGaps: countChangeSchema.extend({ definition: z.string() }),
      closureTime: closureChangeSchema.extend({ definition: z.string() }),
      riskLevelDefinition: z.string(),
    }),
  )

export const populationFamilies = oc
  .route({ method: 'GET', path: '/v2/population/families' })
  .input(periodQuerySchema)
  .output(
    z.object({
      window: windowSchema,
      /** Family groups with at least one active measure, in label order. */
      groups: z.array(
        labelSchema.extend({
          /** Patients with an open finding in the group at the period's end, each once. */
          uniquePatients: z.number().int(),
          openCareGaps: z.number().int(),
          closureTime: closureChangeSchema,
          /** Active measures of the group with at least one open finding. */
          focusAreas: z.number().int(),
          activeMeasures: z.number().int(),
        }),
      ),
      definitions: z.object({
        uniquePatients: z.string(),
        openCareGaps: z.string(),
        closureTime: z.string(),
        focusAreas: z.string(),
      }),
    }),
  )

export const populationMeasures = oc
  .route({ method: 'GET', path: '/v2/population/measures' })
  .input(periodQuerySchema)
  .output(
    z.object({
      window: windowSchema,
      /** Every active measure, ranked (rank 1 first). The first four are Focus Now. */
      measures: z.array(
        labelSchema.extend({
          rank: z.number().int(),
          kind: z.enum(POPULATION_KINDS),
          family: labelSchema,
          group: labelSchema,
          /** The measure's inclusion definition (cpg_measure.definition). */
          definition: z.string(),
          /** Patients with an open finding at the period's end (one finding per patient). */
          openPatients: z.number().int(),
          highPatients: z.number().int(),
          mediumPatients: z.number().int(),
          openedInPeriod: z.number().int(),
          closureTime: closureChangeSchema,
          /** null when either period resolved nothing. */
          status: z.enum(POPULATION_MEASURE_STATUSES).nullable(),
        }),
      ),
      definitions: z.object({
        ranking: z.string(),
        closureTime: z.string(),
        status: z.string(),
      }),
    }),
  )

export const populationTrend = oc
  .route({ method: 'GET', path: '/v2/population/trend' })
  .input(
    periodQuerySchema.extend({
      /** Narrow to one family group, or one measure (not both). */
      groupSlug: z.string().min(1).optional(),
      measureSlug: z.string().min(1).optional(),
      /**
       * Only the slices (a family card's sparkline): skips the latest-slice comparison and
       * the aging, two more reads over the findings history; `latest` is then null and
       * `aging` empty.
       */
      pointsOnly: queryBooleanSchema.optional(),
    }),
  )
  .output(
    z.object({
      window: windowSchema,
      /**
       * The current period in up to six consecutive, near-equal slices of whole days, oldest
       * first — fewer when the period is shorter than six days (year to date on 1–5 January).
       * Each slice's figure is over the findings closed in it.
       */
      points: z.array(dateRangeSchema.extend(closureTimeSchema.shape)),
      /** The last slice next to the comparison period's last slice; null with pointsOnly. */
      latest: z
        .object({
          current: dateRangeSchema.extend(closureTimeSchema.shape),
          comparison: dateRangeSchema.extend(closureTimeSchema.shape),
          changeDays: z.number().nullable(),
        })
        .nullable(),
      /**
       * Findings open at the period's end by whole days open: 0–30, 31–60, 61–90, 91–180 and
       * over 180 (`maxDays` null). Empty with pointsOnly.
       */
      aging: z.array(
        z.object({
          minDays: z.number().int(),
          maxDays: z.number().int().nullable(),
          count: z.number().int(),
        }),
      ),
      definitions: z.object({ trend: z.string(), aging: z.string() }),
    }),
  )

export const populationProviders = oc
  .route({ method: 'GET', path: '/v2/population/providers' })
  .input(periodQuerySchema)
  .output(
    z.object({
      window: windowSchema,
      /** Attributed providers ranked by median time to resolve (fastest first, none closed last). */
      providers: z.array(
        z.object({
          rank: z.number().int(),
          providerId: z.string(),
          providerName: z.string().nullable(),
          panelPatients: z.number().int(),
          openCareGaps: z.number().int(),
          closureTime: closureChangeSchema,
        }),
      ),
      /** Patients in scope with no attributed provider. */
      unattributedPatients: z.number().int(),
      definitions: z.object({
        panelPatients: z.string(),
        openCareGaps: z.string(),
        closureTime: z.string(),
      }),
    }),
  )

/**
 * The State filter's options: address states of the caller's patients, A–Z with 'unknown'
 * last. Takes no filters, so the list doesn't shrink as filters are applied.
 */
export const populationStates = oc
  .route({ method: 'GET', path: '/v2/population/states' })
  .output(z.object({ states: z.array(z.string()) }))

/** Mirrors ModelPageRequest (src/model/types.ts). */
const pageRequestSchema = z.object({
  offset: z.number().int().min(0),
  count: z.number().int().min(0).max(200),
  limit: z.number().int().min(0),
})

const cohortInputSchema = z.object({
  slug: z.string().min(1),
  ...filtersShape,
  /** Case-insensitive match on the patient's name. */
  search: z.string().optional(),
  /** Only patients with a phone or an email. */
  reachableOnly: z.boolean().optional(),
  page: pageRequestSchema,
})

const cohortFindingSchema = z.object({
  measure: labelSchema,
  kind: z.enum(POPULATION_KINDS),
  grade: z.enum(POPULATION_RISK_LEVELS),
  /** The finding text the Risks & Gaps report wrote. */
  finding: z.string(),
  /** The measure's definition: the rule that qualified the patient. */
  qualifyingRule: z.string(),
  openedAt: z.string(),
  /** The latest report that carried the finding. */
  lastEvaluatedAt: z.string(),
})

const cohortOutputSchema = z.object({
  cohort: labelSchema.extend({
    /** The exact inclusion definition used to build the cohort. */
    definition: z.string(),
    totals: z.object({
      patients: z.number().int(),
      highRisk: z.number().int(),
      reachable: z.number().int(),
    }),
  }),
  page: pageRequestSchema.extend({
    size: z.number().int().min(0),
    total: z.number().int().min(0),
  }),
  data: z.array(
    z.object({
      patientId: z.string(),
      name: z.string().nullable(),
      age: z.number().int().nullable(),
      providerId: z.string().nullable(),
      providerName: z.string().nullable(),
      riskLevel: z.enum(POPULATION_RISK_LEVELS),
      reachable: z.boolean(),
      /** Why the patient is in this cohort, highest grade first. */
      inclusionReasons: z.array(cohortFindingSchema),
      /** The patient's other open findings — never a reason for inclusion. */
      otherOpenGaps: z.array(cohortFindingSchema),
    }),
  ),
  definitions: z.object({ riskLevel: z.string(), reachable: z.string() }),
})

export const populationMeasurePatients = oc
  .route({ method: 'POST', path: '/v2/population/measures/{slug}/patients' })
  .input(cohortInputSchema)
  .output(cohortOutputSchema)

/**
 * The cohort as CSV (PD-1775), minus the patients the drawer excluded: one row per patient with
 * age, attributed provider, risk, reachability and inclusion reasons. Open to anyone who may
 * read the cohort (PD-1992); every exported patient gets a download record in their access
 * history.
 */
export const populationCohortExport = oc
  .route({ method: 'POST', path: '/v2/population/cohort-export' })
  .input(
    z.object({
      target: z.enum(['measure', 'group']),
      slug: z.string().min(1),
      ...filtersShape,
      /** Left out of the file: the drawer's exclusions, kept only in the browser. */
      excludedPatientIds: z.array(z.string().min(1)).max(10000).optional(),
    }),
  )
  .output(z.object({ filename: z.string(), csv: z.string(), rows: z.number().int() }))

export const populationFamilyPatients = oc
  .route({ method: 'POST', path: '/v2/population/families/{slug}/patients' })
  .input(cohortInputSchema)
  .output(cohortOutputSchema)
